import React, { useState } from 'react';
import {
  IonContent,
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonIcon,
  IonItem,
  IonLabel,
  IonInput,
  IonTextarea,
  IonDatetime,
  IonModal,
  IonText,
  IonSpinner,
  IonCard,
  IonCardContent,
  IonProgressBar,
  IonToast
} from '@ionic/react';
import {
  arrowBack,
  cloudUploadOutline,
  videocamOutline,
  imageOutline,
  closeOutline,
  expandOutline,
  calendarOutline,
  timeOutline,
  locationOutline
} from 'ionicons/icons';
import { useHistory } from 'react-router-dom';
import { auth } from '../config/firebase';
import {
  uploadImage,
  uploadVideo,
  validateImageFile,
  validateVideoFile
} from '../services/storageService';
import { createEvent } from '../services/eventsService';
import { EventFormData, Currency } from '../types/event.types';
import LocationPickerModal from '../components/LocationPickerModal';
import PixPaymentModal from '../components/PixPaymentModal';
import { useTranslation } from '../hooks/useTranslation';
import { getAppConfig, getCurrencies } from '../services/configService';
import { generateToken, saveToken } from '../services/tokenService';
import { sendTokenEmail } from '../services/emailService';
import './CreateEventPage.css';

const CreateEventPage: React.FC = () => {
  const history = useHistory();
  const user = auth.currentUser;
  const { t } = useTranslation();

  const [formData, setFormData] = useState<EventFormData>({
    title: '',
    description: '',
    date: new Date().toISOString(),
    startTime: '18:00',
    endTime: '23:00',
    price: '',
    currency: '',
    address: '',
    lat: 0,
    lng: 0
  });
  const [currencies] = useState<Currency[]>(() => getCurrencies());
  const [locationConfirmed, setLocationConfirmed] = useState(false);

  const [photoHorizontal, setPhotoHorizontal] = useState<File | null>(null);
  const [photoVertical, setPhotoVertical] = useState<File | null>(null);
  const [video, setVideo] = useState<File | null>(null);

  const [photoHorizontalPreview, setPhotoHorizontalPreview] = useState<string>('');
  const [photoVerticalPreview, setPhotoVerticalPreview] = useState<string>('');
  const [videoPreview, setVideoPreview] = useState<string>('');

  const [loading, setLoading] = useState(false);
  const [validatingVideo, setValidatingVideo] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadMessage, setUploadMessage] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});


  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastColor, setToastColor] = useState<'success' | 'danger' | 'warning'>('success');
  const [toastIsSuccess, setToastIsSuccess] = useState(false);

  const [previewModal, setPreviewModal] = useState<{ src: string; type: 'image' | 'video' } | null>(null);
  const [showPixModal, setShowPixModal] = useState(false);
  const [createdEventId, setCreatedEventId] = useState('');
  const [pixAmount, setPixAmount] = useState(29.90);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showStartTimePicker, setShowStartTimePicker] = useState(false);
  const [showEndTimePicker, setShowEndTimePicker] = useState(false);
  const [showLocationPicker, setShowLocationPicker] = useState(false);

  const photoHorizontalInputRef = React.useRef<HTMLInputElement>(null);
  const photoVerticalInputRef = React.useRef<HTMLInputElement>(null);
  const videoInputRef = React.useRef<HTMLInputElement>(null);

  // Solicitar geolocalización al entrar y pre-centrar el mapa
  React.useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      pos => {
        setFormData(prev => ({ ...prev, lat: pos.coords.latitude, lng: pos.coords.longitude }));
        setShowLocationPicker(true);
      },
      () => {
        // Si el usuario deniega, abrir el mapa igualmente centrado en Guatemala
        setFormData(prev => ({ ...prev, lat: 14.6349, lng: -90.5069 }));
        setShowLocationPicker(true);
      },
      { enableHighAccuracy: true, timeout: 8000 }
    );
  }, []); // eslint-disable-line

  const formatDisplayDate = (isoDate: string) => {
    try {
      const date = new Date(isoDate);
      return date.toLocaleDateString('es-GT', { day: 'numeric', month: 'long', year: 'numeric' });
    } catch {
      return t('create_event.select_date');
    }
  };

  const formatDisplayTime = (time: string) => {
    if (!time) return t('common.select');
    const [h, m] = time.split(':');
    const hour = parseInt(h);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${m} ${ampm}`;
  };

  const handleInputChange = (field: keyof EventFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handlePhotoHorizontalChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const error = validateImageFile(file);
    if (error) {
      setErrors(prev => ({ ...prev, photoHorizontal: t(error) }));
      return;
    }

    setPhotoHorizontal(file);
    setPhotoHorizontalPreview(URL.createObjectURL(file));
    setErrors(prev => ({ ...prev, photoHorizontal: '' }));
  };

  const handlePhotoVerticalChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const error = validateImageFile(file);
    if (error) {
      setErrors(prev => ({ ...prev, photoVertical: t(error) }));
      return;
    }

    setPhotoVertical(file);
    setPhotoVerticalPreview(URL.createObjectURL(file));
    setErrors(prev => ({ ...prev, photoVertical: '' }));
  };

  const handleVideoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setValidatingVideo(true);
    const error = await validateVideoFile(file);
    setValidatingVideo(false);

    if (error) {
      setErrors(prev => ({ ...prev, video: t(error) }));
      return;
    }

    setVideo(file);
    setVideoPreview(URL.createObjectURL(file));
    setErrors(prev => ({ ...prev, video: '' }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) newErrors.title = t('validation.title_required');
    if (!formData.description.trim()) newErrors.description = t('validation.description_required');
    if (!formData.currency.trim()) newErrors.currency = t('validation.currency_required');
    if (!formData.price || parseFloat(formData.price) < 0) newErrors.price = t('validation.price_invalid');
    if (!formData.address.trim()) newErrors.address = t('validation.address_required');
    if (!locationConfirmed) newErrors.location = t('validation.location_required');
    if (!photoHorizontal) newErrors.photoHorizontal = t('validation.photo_horizontal_required');
    if (!photoVertical) newErrors.photoVertical = t('validation.photo_vertical_required');
    if (!video) newErrors.video = t('validation.video_required');

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm() || !user) return;

    setLoading(true);
    setUploadProgress(0);
    setUploadMessage('');

    try {
      setUploadProgress(10);
      setUploadMessage(t('create_event.uploading_horizontal'));
      const photoItemURL = await uploadImage(photoHorizontal!, user.uid, 'horizontal');

      setUploadProgress(40);
      setUploadMessage(t('create_event.uploading_vertical'));
      const photoPortraitURL = await uploadImage(photoVertical!, user.uid, 'vertical');

      setUploadProgress(70);
      setUploadMessage(t('create_event.uploading_video'));
      const videoURL = await uploadVideo(video!, user.uid);

      setUploadProgress(85);
      setUploadMessage(t('create_event.creating_event'));

      const appConfig = await getAppConfig();

      const eventId = await createEvent({
        information: formData.title,
        information2: formData.description,
        date: new Date(formData.date),
        schedule_start: formData.startTime,
        schedule_finish: formData.endTime,
        price: parseFloat(formData.price),
        currency: formData.currency,
        address_id: formData.address,
        lat: formData.lat,
        long: formData.lng,
        photoItem: photoItemURL,
        photoPortrait: photoPortraitURL,
        videoUrl: videoURL,
        createdBy: user.uid,
        createdByEmail: user.email || '',
      });

      setUploadProgress(100);
      setUploadMessage(t('create_event.event_created'));
      setLoading(false);

      if (!appConfig.enableCharging) {
        // Sin cobro: el moderador publica directamente al aprobar
        setTimeout(() => {
          setToastMessage(t('create_event.success_free'));
          setToastColor('success');
          setToastIsSuccess(true);
          setShowToast(true);
        }, 800);
      } else {
        // Pago requerido: mostrar modal Pix
        setCreatedEventId(eventId);
        setPixAmount((appConfig as any).pixAmount || 29.90);
        setTimeout(() => setShowPixModal(true), 600);
      }

    } catch (error: any) {
      console.error('Error creando evento:', error);
      setToastMessage(t('create_event.error_create') + (error.message || ''));
      setToastColor('danger');
      setToastIsSuccess(false);
      setShowToast(true);
      setLoading(false);
      setUploadProgress(0);
      setUploadMessage('');
    }
  };

  const handlePixConfirm = async () => {
    setShowPixModal(false);
    setToastMessage(t('pix.pending_notice'));
    setToastColor('warning');
    setToastIsSuccess(true);
    setShowToast(true);
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar className="cms-toolbar">
          <IonButtons slot="start">
            <IonButton onClick={() => history.goBack()} disabled={loading} className="cms-back-btn">
              <IonIcon icon={arrowBack} />
            </IonButton>
          </IonButtons>
          <IonTitle>{t('create_event.title')}</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent className="create-event-content">
        <div className="form-container">

          {/* Upload Progress */}
          {loading && (
            <div className="upload-progress">
              <IonSpinner name="crescent" color="primary" />
              <p>{uploadMessage || t('create_event.uploading_files')}</p>
              <div className="progress-bar-container">
                <IonProgressBar value={uploadProgress / 100} color="primary" />
                <span className="progress-percentage">{uploadProgress}%</span>
              </div>
            </div>
          )}

          {/* Validating Video */}
          {validatingVideo && (
            <div className="validating-video">
              <IonSpinner name="crescent" color="primary" />
              <p>{t('create_event.validating_video')}</p>
            </div>
          )}

          {/* Form Grid */}
          <div className="form-grid">

            {/* Titulo */}
            <div className="form-grid-full">
              <IonCard>
                <IonCardContent>
                  <IonItem lines="none">
                    <IonLabel position="stacked">
                      {t('create_event.event_title_label')} <span className="required">*</span>
                    </IonLabel>
                    <IonInput
                      value={formData.title}
                      onIonChange={(e) => handleInputChange('title', e.detail.value)}
                      placeholder={t('create_event.event_title_placeholder')}
                      disabled={loading}
                    />
                  </IonItem>
                  {errors.title && <IonText color="danger"><p className="error-text">{errors.title}</p></IonText>}
                </IonCardContent>
              </IonCard>
            </div>

            {/* Descripcion */}
            <div className="form-grid-full">
              <IonCard>
                <IonCardContent>
                  <IonItem lines="none">
                    <IonLabel position="stacked">
                      {t('create_event.description_label')} <span className="required">*</span>
                    </IonLabel>
                    <IonTextarea
                      value={formData.description}
                      onIonChange={(e) => handleInputChange('description', e.detail.value)}
                      placeholder={t('create_event.description_placeholder')}
                      rows={4}
                      disabled={loading}
                    />
                  </IonItem>
                  {errors.description && <IonText color="danger"><p className="error-text">{errors.description}</p></IonText>}
                </IonCardContent>
              </IonCard>
            </div>

            {/* Fecha */}
            <IonCard>
              <IonCardContent>
                <IonLabel position="stacked" className="picker-label">{t('create_event.event_date')}</IonLabel>
                <div className="picker-field" onClick={() => !loading && setShowDatePicker(true)}>
                  <IonIcon icon={calendarOutline} className="picker-field-icon" />
                  <span>{formatDisplayDate(formData.date)}</span>
                </div>
              </IonCardContent>
            </IonCard>

            {/* Horas */}
            <IonCard>
              <IonCardContent>
                <div className="time-row">
                  <div>
                    <IonLabel position="stacked" className="picker-label">{t('create_event.start_time')}</IonLabel>
                    <div className="picker-field" onClick={() => !loading && setShowStartTimePicker(true)}>
                      <IonIcon icon={timeOutline} className="picker-field-icon" />
                      <span>{formatDisplayTime(formData.startTime)}</span>
                    </div>
                  </div>
                  <div>
                    <IonLabel position="stacked" className="picker-label">{t('create_event.end_time')}</IonLabel>
                    <div className="picker-field" onClick={() => !loading && setShowEndTimePicker(true)}>
                      <IonIcon icon={timeOutline} className="picker-field-icon" />
                      <span>{formatDisplayTime(formData.endTime)}</span>
                    </div>
                  </div>
                </div>
              </IonCardContent>
            </IonCard>

            {/* Date Picker Modal */}
            <IonModal isOpen={showDatePicker} onDidDismiss={() => setShowDatePicker(false)} className="datetime-modal">
              <div className="datetime-modal-content">
                <div className="datetime-modal-header">
                  <h3>{t('create_event.select_date')}</h3>
                  <IonButton fill="clear" onClick={() => setShowDatePicker(false)}>{t('common.done')}</IonButton>
                </div>
                <IonDatetime
                  value={formData.date}
                  onIonChange={(e) => handleInputChange('date', e.detail.value)}
                  presentation="date"
                  min={new Date().toISOString()}
                />
              </div>
            </IonModal>

            {/* Start Time Picker Modal */}
            <IonModal isOpen={showStartTimePicker} onDidDismiss={() => setShowStartTimePicker(false)} className="datetime-modal">
              <div className="datetime-modal-content">
                <div className="datetime-modal-header">
                  <h3>{t('create_event.start_time_title')}</h3>
                  <IonButton fill="clear" onClick={() => setShowStartTimePicker(false)}>{t('common.done')}</IonButton>
                </div>
                <IonDatetime
                  value={`2024-01-01T${formData.startTime}:00`}
                  onIonChange={(e) => {
                    const val = e.detail.value as string;
                    const time = val.split('T')[1]?.substring(0, 5) || formData.startTime;
                    handleInputChange('startTime', time);
                  }}
                  presentation="time"
                />
              </div>
            </IonModal>

            {/* End Time Picker Modal */}
            <IonModal isOpen={showEndTimePicker} onDidDismiss={() => setShowEndTimePicker(false)} className="datetime-modal">
              <div className="datetime-modal-content">
                <div className="datetime-modal-header">
                  <h3>{t('create_event.end_time_title')}</h3>
                  <IonButton fill="clear" onClick={() => setShowEndTimePicker(false)}>{t('common.done')}</IonButton>
                </div>
                <IonDatetime
                  value={`2024-01-01T${formData.endTime}:00`}
                  onIonChange={(e) => {
                    const val = e.detail.value as string;
                    const time = val.split('T')[1]?.substring(0, 5) || formData.endTime;
                    handleInputChange('endTime', time);
                  }}
                  presentation="time"
                />
              </div>
            </IonModal>

            {/* Moneda */}
            {/* Moneda + Precio */}
            <IonCard>
              <IonCardContent>
                <IonItem lines="none">
                  <IonLabel position="stacked">
                    {t('create_event.price_amount_label')} <span className="required">*</span>
                  </IonLabel>
                  <div className="price-row">
                    <input
                      className="currency-combobox"
                      list="currency-options"
                      value={formData.currency}
                      onChange={e => handleInputChange('currency', e.target.value)}
                      placeholder={t('create_event.currency_label')}
                      disabled={loading}
                      required
                    />
                    <datalist id="currency-options">
                      {currencies.map(c => (
                        <option key={c.code} value={c.symbol}>{c.symbol} – {c.name} ({c.code})</option>
                      ))}
                    </datalist>
                    <IonInput
                      type="number"
                      value={formData.price}
                      onIonChange={(e) => handleInputChange('price', e.detail.value)}
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                      disabled={loading}
                    />
                  </div>
                </IonItem>
                {errors.currency && <IonText color="danger"><p className="error-text">{errors.currency}</p></IonText>}
                {errors.price && <IonText color="danger"><p className="error-text">{errors.price}</p></IonText>}
              </IonCardContent>
            </IonCard>

            {/* Direccion */}
            <IonCard>
              <IonCardContent>
                <IonLabel position="stacked" className="picker-label">
                  {t('create_event.address_label')} <span className="required">*</span>
                </IonLabel>
                {/* Botón abrir mapa */}
                <button
                  type="button"
                  className={`map-confirm-btn${locationConfirmed ? ' map-confirm-btn--ok' : ''}`}
                  onClick={() => !loading && setShowLocationPicker(true)}
                  disabled={loading}
                >
                  <IonIcon icon={locationOutline} />
                  {locationConfirmed
                    ? `✓ ${t('create_event.location_confirmed')} (${formData.lat.toFixed(4)}, ${formData.lng.toFixed(4)})`
                    : t('create_event.location_pick')}
                </button>
                {errors.location && <IonText color="danger"><p className="error-text">{errors.location}</p></IonText>}
                {/* Dirección de texto */}
                <div className="picker-field address-picker-field" style={{ marginTop: 8 }}>
                  <IonIcon icon={locationOutline} className="picker-field-icon address-location-icon" />
                  <input
                    type="text"
                    className="address-native-input"
                    value={formData.address}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    placeholder={t('create_event.address_placeholder')}
                    disabled={loading}
                  />
                </div>
                {errors.address && <IonText color="danger"><p className="error-text">{errors.address}</p></IonText>}
              </IonCardContent>
            </IonCard>

          </div>

          {/* Media Grid */}
          <div className="media-grid">

        {/* Foto Horizontal */}
        <IonCard className="media-grid-item">
          <IonCardContent>
            <div className="upload-section">
              <h3>
                <IonIcon icon={imageOutline} /> {t('create_event.photo_horizontal')} <span className="required">*</span>
              </h3>
              <p className="helper-text">{t('create_event.photo_horizontal_hint')}</p>

              {photoHorizontalPreview ? (
                <div className="preview-container">
                  <img
                    src={photoHorizontalPreview}
                    alt="Preview horizontal"
                    className="preview-thumbnail"
                    onClick={() => setPreviewModal({ src: photoHorizontalPreview, type: 'image' })}
                  />
                  <div className="preview-actions">
                    <IonButton fill="clear" size="small" onClick={() => setPreviewModal({ src: photoHorizontalPreview, type: 'image' })}>
                      <IonIcon icon={expandOutline} slot="start" />
                      {t('common.view')}
                    </IonButton>
                    <IonButton
                      fill="clear"
                      size="small"
                      onClick={() => { setPhotoHorizontal(null); setPhotoHorizontalPreview(''); }}
                      disabled={loading}
                    >
                      {t('common.change')}
                    </IonButton>
                  </div>
                </div>
              ) : (
                <>
                  <input
                    ref={photoHorizontalInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoHorizontalChange}
                    disabled={loading || validatingVideo}
                    style={{ display: 'none' }}
                  />
                  <IonButton
                    expand="block"
                    disabled={loading || validatingVideo}
                    onClick={() => photoHorizontalInputRef.current?.click()}
                  >
                    <IonIcon icon={cloudUploadOutline} slot="start" />
                    {t('common.upload')}
                  </IonButton>
                </>
              )}
              {errors.photoHorizontal && <IonText color="danger"><p className="error-text">{errors.photoHorizontal}</p></IonText>}
            </div>
          </IonCardContent>
        </IonCard>

          {/* Foto Vertical */}
          <IonCard className="media-grid-item">
            <IonCardContent>
              <div className="upload-section">
                <h3>
                  <IonIcon icon={imageOutline} /> {t('create_event.photo_vertical')} <span className="required">*</span>
                </h3>
                <p className="helper-text">{t('create_event.photo_vertical_hint')}</p>

                {photoVerticalPreview ? (
                  <div className="preview-container">
                    <img
                      src={photoVerticalPreview}
                      alt="Preview vertical"
                      className="preview-thumbnail"
                      onClick={() => setPreviewModal({ src: photoVerticalPreview, type: 'image' })}
                    />
                    <div className="preview-actions">
                      <IonButton fill="clear" size="small" onClick={() => setPreviewModal({ src: photoVerticalPreview, type: 'image' })}>
                        <IonIcon icon={expandOutline} slot="start" />
                        {t('common.view')}
                      </IonButton>
                      <IonButton
                        fill="clear"
                        size="small"
                        onClick={() => { setPhotoVertical(null); setPhotoVerticalPreview(''); }}
                        disabled={loading}
                      >
                        {t('common.change')}
                      </IonButton>
                    </div>
                  </div>
                ) : (
                  <>
                    <input
                      ref={photoVerticalInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoVerticalChange}
                      disabled={loading || validatingVideo}
                      style={{ display: 'none' }}
                    />
                    <IonButton
                      expand="block"
                      disabled={loading || validatingVideo}
                      onClick={() => photoVerticalInputRef.current?.click()}
                    >
                      <IonIcon icon={cloudUploadOutline} slot="start" />
                      {t('common.upload')}
                    </IonButton>
                  </>
                )}
                {errors.photoVertical && <IonText color="danger"><p className="error-text">{errors.photoVertical}</p></IonText>}
              </div>
            </IonCardContent>
          </IonCard>

        {/* Video */}
        <IonCard className="media-grid-item">
          <IonCardContent>
            <div className="upload-section">
              <h3>
                <IonIcon icon={videocamOutline} /> {t('create_event.video')} <span className="required">*</span>
              </h3>
              <p className="helper-text">{t('create_event.video_hint')}</p>

              {videoPreview ? (
                <div className="preview-container">
                  <video src={videoPreview} className="preview-thumbnail" onClick={() => setPreviewModal({ src: videoPreview, type: 'video' })} />
                  <div className="preview-actions">
                    <IonButton fill="clear" size="small" onClick={() => setPreviewModal({ src: videoPreview, type: 'video' })}>
                      <IonIcon icon={expandOutline} slot="start" />
                      {t('common.view')}
                    </IonButton>
                    <IonButton
                      fill="clear"
                      size="small"
                      onClick={() => { setVideo(null); setVideoPreview(''); }}
                      disabled={loading}
                    >
                      {t('common.change')}
                    </IonButton>
                  </div>
                </div>
              ) : (
                <>
                  <input
                    ref={videoInputRef}
                    type="file"
                    accept="video/*"
                    onChange={handleVideoChange}
                    disabled={loading || validatingVideo}
                    style={{ display: 'none' }}
                  />
                  <IonButton
                    expand="block"
                    disabled={loading || validatingVideo}
                    onClick={() => videoInputRef.current?.click()}
                  >
                    {validatingVideo ? (
                      <IonSpinner name="crescent" />
                    ) : (
                      <>
                        <IonIcon icon={cloudUploadOutline} slot="start" />
                        {t('common.upload')}
                      </>
                    )}
                  </IonButton>
                </>
              )}
              {errors.video && <IonText color="danger"><p className="error-text">{errors.video}</p></IonText>}
            </div>
          </IonCardContent>
        </IonCard>

          </div>

          {/* Submit Button */}
          <div className="submit-container">
            <IonButton
              expand="block"
              size="large"
              onClick={handleSubmit}
              disabled={loading || validatingVideo}
              className="submit-button"
            >
              {loading ? (
                <>
                  <IonSpinner name="crescent" />
                  <span style={{ marginLeft: '8px' }}>{t('create_event.submit_creating')}</span>
                </>
              ) : (
                t('create_event.submit_create')
              )}
            </IonButton>
          </div>

        </div>
      </IonContent>

      <IonToast
        isOpen={showToast}
        onDidDismiss={() => {
          setShowToast(false);
          if (toastIsSuccess) {
            history.replace('/dashboard');
          }
        }}
        message={toastMessage}
        duration={3000}
        color={toastColor}
        position="top"
      />

      {previewModal && (
        <div className="media-preview-overlay" onClick={() => setPreviewModal(null)}>
          <button className="media-preview-close" onClick={() => setPreviewModal(null)}>
            <IonIcon icon={closeOutline} />
          </button>
          <div className="media-preview-modal" onClick={(e) => e.stopPropagation()}>
            {previewModal.type === 'image' ? (
              <img src={previewModal.src} alt="Preview completo" />
            ) : (
              <video src={previewModal.src} controls autoPlay />
            )}
          </div>
        </div>
      )}

      {showPixModal && (
        <PixPaymentModal
          eventName={formData.title}
          amount={pixAmount}
          onConfirm={handlePixConfirm}
          onClose={() => setShowPixModal(false)}
        />
      )}

      <LocationPickerModal
        isOpen={showLocationPicker}
        lat={formData.lat}
        lng={formData.lng}
        onConfirm={(lat, lng) => {
          setFormData(prev => ({ ...prev, lat, lng }));
          setLocationConfirmed(true);
          setShowLocationPicker(false);
        }}
        onClose={() => setShowLocationPicker(false)}
      />
    </IonPage>
  );
};

export default CreateEventPage;
